import type { UIEvent } from 'react';
import {
  startTransition,
  useCallback,
  useEffect,
  useRef,
  useState,
} from 'react';
import { motion } from 'framer-motion';
import type {
  GetWorkspacesListQuery,
  GetWorkspacesQuery,
} from '@frameio/graphql-client/generated';
import { SearchFieldWithI18n } from '@frameio/shared/components/SearchField';
import {
  Box,
  noop,
  ScrollContainer,
  Spinner,
  Text,
  theme,
  useSheetInitialFocusRef,
} from '@frameio/vapor';
import { useCreateWorkspacesList } from '../../hooks';
import { WorkspaceGroup } from './components';
import { RecentProjectsGroup } from './components/WorkspaceGroup';
import TEXT from './generated/TEXT/WorkspaceProjectList';
import type {
  ProjectItemVariant,
  WorkspaceProjectSelectCallback,
} from './types';

TEXT.DEFINE({
  /** Placeholder text for project search */
  searchPlaceholder: 'Find project',
  /** Empty state message when no projects match search */
  searchEmptyState: 'No projects matching',
  /** Empty state query text for search */
  searchEmtpyStateQuery: (query: string) => `"${query}"`,
  /** Aria label for the spinner */
  spinnerAriaLabel: 'Spinner',
});

type WorkspaceProjectListProps = {
  data: GetWorkspacesQuery | GetWorkspacesListQuery;
  onWorkspaceProjectSelect: WorkspaceProjectSelectCallback;
  projectItemVariant?: ProjectItemVariant;
  /**
   * @todo (ASSETS-489):
   * This prop is temporary, in order to disable the loading state when
   * rendered in the ProjectSwitcher component, until this has been resolved.
   */
  disableLoadingSpinner?: boolean;
  compactProjectGroups?: boolean;
  activeProjectId?: string;
  loadingProjectId?: string;
  navOnClick?: boolean;
  onScroll?: (event: UIEvent<HTMLElement>) => void;
};

export function WorkspaceProjectList({
  data,
  onWorkspaceProjectSelect,
  projectItemVariant,
  disableLoadingSpinner = false,
  compactProjectGroups = false,
  loadingProjectId,
  activeProjectId,
  navOnClick,
  onScroll = noop,
}: WorkspaceProjectListProps) {
  /**
   * This useState tracks an array of workspaces whose projects are currently being loaded.
   *
   * The WorkspaceGroup component (responsible for fetching its associated workspace's projects) will register itself by adding its associated workspaceId to the loadingWorkspacesIds array.
   *
   * Once that WorkspaceGroup's projects have loaded, it will trigger an update that will filter the associated workspaceId from the loadingWorkspacesIds array.
   *
   * All Workspaces (and their associated projects) can be considered loaded
   * once the loadingWorkspacesIds array is empty.
   */
  const [loadingWorkspacesIds, setLoadingWorkspacesIds] = useState<
    string[] | null
  >(null);
  const [isInitialLoading, setIsInitialLoading] = useState(
    loadingWorkspacesIds === null || loadingWorkspacesIds.length > 0
  );

  const handleLoadingChange = useCallback(
    (isLoading: boolean, workspaceId: string): void => {
      if (isLoading) {
        return setLoadingWorkspacesIds((prevState) =>
          prevState === null ? [workspaceId] : [...prevState, workspaceId]
        );
      }
      setIsInitialLoading(false);
      return setLoadingWorkspacesIds((prevState) =>
        prevState?.length ? prevState.filter((id) => id !== workspaceId) : []
      );
    },
    []
  );

  const workspacesList = useCreateWorkspacesList(data);
  const workspacesAndProjectsAreLoading = !disableLoadingSpinner
    ? isInitialLoading
    : false;
  const ref = useSheetInitialFocusRef();
  const [query, setQuery] = useState<string>('');
  const prevQueryRef = useRef(query);

  const [
    availableProjectCountOnWorkspaces,
    setAvailableProjectCountOnWorkspaces,
  ] = useState<Record<string, number>>({});

  const handleAvailableProjectCountChange = useCallback(
    (count: number, workspaceId?: string) => {
      setAvailableProjectCountOnWorkspaces((prevState) => {
        if (!workspaceId) return prevState;
        return {
          ...prevState,
          [workspaceId]: count,
        };
      });
    },
    []
  );

  const [shouldShowSearchEmptyState, setShouldShowSearchEmptyState] =
    useState(false);

  useEffect(() => {
    if (prevQueryRef.current !== query && shouldShowSearchEmptyState) {
      prevQueryRef.current = query;
      setShouldShowSearchEmptyState(false);
      return;
    }
    setShouldShowSearchEmptyState(
      Boolean(query) &&
        Object.values(availableProjectCountOnWorkspaces).every(
          (count) => count === 0
        )
    );
  }, [availableProjectCountOnWorkspaces, query, shouldShowSearchEmptyState]);

  if (!workspacesList) return null;

  /**
   * Workspaces with no projects are omitted from this view,
   * but can still be seen on the Workspaces page
   */
  const workspacesWithProjects = workspacesList.filter(
    (ws) => ws.projectCount && ws.projectCount > 0
  );

  return (
    <ScrollContainer
      height="100%"
      onScroll={onScroll}
      overflow="hidden"
      paddingX={SCROLL_CONTAINER_PADDING_X}
      paddingY={SCROLL_CONTAINER_PADDING_Y}
    >
      <Box
        style={{ marginTop: theme.spaces[16], marginBottom: theme.spaces[8] }}
      >
        <SearchFieldWithI18n
          ref={ref}
          aria-autocomplete="list"
          aria-controls="search-list"
          autoFocus={true}
          enableSpinner={false}
          onQueryChange={(value) => {
            startTransition(() => {
              setQuery(value);
            });
          }}
          placeholder={TEXT.searchPlaceholder}
        />
      </Box>
      <Box paddingBottom={8}>
        <Spinner
          aria-label={TEXT.spinnerAriaLabel}
          centered={true}
          delay={100}
          isLoading={workspacesAndProjectsAreLoading}
        />
        <motion.div
          animate={{ opacity: workspacesAndProjectsAreLoading ? 0 : 1 }}
          initial={{ opacity: 0 }}
          transition={FADE_IN_TRANSITION}
        >
          {shouldShowSearchEmptyState ? (
            <Box
              alignItems="center"
              display="flex"
              flexDirection="column"
              left="50%"
              position="absolute"
              top="50%"
              transform="translate(-50%, -50%)"
              width="100%"
            >
              <Text color="tertiary" variant="body">
                {TEXT.searchEmptyState}
              </Text>
              <Text variant="body">{TEXT.searchEmtpyStateQuery(query)}</Text>
            </Box>
          ) : (
            <>
              <RecentProjectsGroup
                onWorkspaceProjectSelect={onWorkspaceProjectSelect}
              />
              {workspacesWithProjects.map((workspace) => (
                <WorkspaceGroup
                  key={workspace.id}
                  activeProjectId={activeProjectId}
                  compact={compactProjectGroups}
                  loadingProjectId={loadingProjectId}
                  navOnClick={navOnClick}
                  onAvailableProjectCountChange={
                    handleAvailableProjectCountChange
                  }
                  onLoadingChange={handleLoadingChange}
                  onWorkspaceProjectSelect={onWorkspaceProjectSelect}
                  projectItemVariant={projectItemVariant}
                  query={query}
                  workspace={workspace}
                />
              ))}
            </>
          )}
        </motion.div>
      </Box>
    </ScrollContainer>
  );
}

const FADE_IN_TRANSITION = {
  duration: 0.25,
  ease: theme.transitions.curves.easeOut,
};
const SCROLL_CONTAINER_PADDING_X = 16;
const SCROLL_CONTAINER_PADDING_Y = 0;
